import { Injectable, NgZone, computed, signal } from "@angular/core";
import { Router } from "@angular/router";
import * as envModule from "../../../environments/environment";
import {
  IdleHistoryEvent,
  IdleHistoryEventType,
  IdleLogoutReason,
  IdleSessionConfig,
  IdleSessionPhase,
  IdleSessionSnapshot,
  IdleWorkerClientMessage,
  IdleWorkerClientPayload,
  IdleWorkerServerMessage,
} from "./idle-session.types";

interface LogoutDecision {
  logoutEpoch: number;
  executorTabId: string | null;
}

interface IdleActivityOptions {
  force?: boolean;
}

@Injectable({ providedIn: "root" })
export class IdleSessionService {
  private readonly workerUrl = "/idle-session.worker.js";
  private readonly tabId = this.getOrCreateTabId();
  private readonly sessionId = signal<string | null>(null);
  private readonly snapshotState = signal<IdleSessionSnapshot | null>(null);
  private readonly warningUiEnabledState = signal(true);
  private readonly currentTime = signal(Date.now());
  private readonly logoutDecision = signal<LogoutDecision | null>(null);
  private workerListenersBound = false;
  private activityListenersBound = false;
  private tickHandle: number | null = null;
  private lastTickAt = 0;

  // A tick gap larger than this implies the tab was throttled/suspended rather
  // than merely a slow frame, so we re-evaluate the deadline on the next tick.
  private static readonly FREEZE_GAP_MS = 5000;

  readonly snapshot = computed(() => this.snapshotState());
  readonly warningUiEnabled = computed(() => this.warningUiEnabledState());
  readonly phase = computed<IdleSessionPhase>(() => {
    const snapshot = this.snapshotState();

    if (!snapshot) {
      return "active";
    }

    if (snapshot.logoutInProgress || this.currentTime() >= snapshot.expiresAt) {
      return "timedOut";
    }

    if (this.currentTime() >= snapshot.warningAt) {
      return "warning";
    }

    return "active";
  });
  readonly countdownSeconds = computed(() => {
    const snapshot = this.snapshotState();

    if (!snapshot) {
      return 0;
    }

    return Math.max(
      0,
      Math.ceil((snapshot.expiresAt - this.currentTime()) / 1000),
    );
  });
  readonly logoutInProgress = computed(
    () => this.snapshotState()?.logoutInProgress ?? false,
  );

  constructor(
    private router: Router,
    private zone: NgZone,
  ) {}

  async startSession(sessionId: string | null | undefined): Promise<void> {
    if (!sessionId || !this.canUseServiceWorker()) {
      return;
    }

    if (this.sessionId() === sessionId && this.workerListenersBound) {
      this.requestStateSync();
      return;
    }

    this.sessionId.set(sessionId);
    this.snapshotState.set(null);
    this.logoutDecision.set(null);
    this.bindWorkerListeners();
    this.bindActivityListeners();
    this.startTicker();

    const isReady = await this.waitForWorkerReady();
    if (!isReady) {
      return;
    }

    this.postMessage({
      type: "CLIENT_READY",
      visible: document.visibilityState === "visible",
      config: this.getConfig(),
    });
    this.requestStateSync();
  }

  stopSession(): void {
    this.clearLocalState();
    this.sessionId.set(null);
  }

  clearLocalState(): void {
    this.snapshotState.set(null);
    this.logoutDecision.set(null);
  }

  resetSessionState(sessionId?: string | null): void {
    const targetSessionId = sessionId || this.sessionId();

    if (!targetSessionId) {
      this.clearLocalState();
      return;
    }

    this.clearLocalState();
    void this.clearPersistedSessionState(targetSessionId);
  }

  setWarningUiEnabled(enabled: boolean): void {
    this.warningUiEnabledState.set(enabled);
  }

  /**
   * Single entry point for every signal that could reset the idle deadline:
   * pointer/keyboard input AND wake events (focus/visibility/pageshow/resume).
   * They all obey the same rule, keyed off the phase derived from real
   * wall-clock time:
   *
   *   active   → reset the deadline (fresh full window)
   *   warning  → do NOT reset; only an explicit force (Stay signed in) may
   *   timedOut → NEVER reset; hand off to the worker to coordinate logout
   *
   * The wall-clock refresh below is load-bearing. `phase()` reads `currentTime`,
   * which is advanced only by the 1s ticker; while a tab is throttled or frozen
   * the ticker stops, so an event arriving on wake would otherwise be judged
   * against a stale clock and could resurrect an already-expired session — the
   * exact failure this control is meant to prevent.
   */
  recordActivity(
    source = "user-activity",
    options: IdleActivityOptions = {},
  ): void {
    if (!this.sessionId() || this.logoutInProgress()) {
      return;
    }

    this.currentTime.set(Date.now());

    const phase = this.phase();

    if (phase === "timedOut") {
      // Deadline already elapsed. Neither input nor regaining focus may extend
      // it — ask the worker to coordinate the (multi-tab) logout.
      this.postMessage({
        type: "REQUEST_EVALUATION",
        visible: document.visibilityState === "visible",
        config: this.getConfig(),
      });
      return;
    }

    if (phase === "warning" && !options.force) {
      // During the warning countdown only an explicit "Stay signed in" extends
      // the session. Re-sync in case another tab reset it while we were hidden.
      this.requestStateSync();
      return;
    }

    this.applyLocalActivity();

    this.postMessage({
      type: "USER_ACTIVITY",
      source,
      visible: document.visibilityState === "visible",
      config: this.getConfig(),
    });
  }

  requestLogout(reason: IdleLogoutReason): void {
    if (!this.sessionId()) {
      this.routeToLogout();
      return;
    }

    this.applyLocalLogout(reason);

    this.postMessage({
      type: "MANUAL_LOGOUT",
      reason,
      visible: document.visibilityState === "visible",
      config: this.getConfig(),
    });
  }

  isLogoutExecutor(): boolean {
    return this.logoutDecision()?.executorTabId === this.tabId;
  }

  /**
   * Whether THIS tab is responsible for actually completing the logout redirect.
   *
   * The worker can start a logout with no executor assigned (executorTabId ===
   * null) when the deadline elapsed while every tab was closed/suspended, so no
   * connected client could be chosen. In that case the first tab to observe the
   * `logoutInProgress` snapshot must adopt the logout and complete it — otherwise
   * the user lands on /logout but MSAL is never signed out, leaving a live
   * session behind a "logged out" screen. A missing decision means an
   * uncoordinated (e.g. manual) logout, which this tab also executes.
   */
  shouldExecuteLogout(): boolean {
    const decision = this.logoutDecision();

    if (!decision) {
      return true;
    }

    return (
      decision.executorTabId === null || decision.executorTabId === this.tabId
    );
  }

  hasLogoutDecision(): boolean {
    return this.logoutDecision() !== null;
  }

  isLogoutInProgressValue(): boolean {
    return this.logoutInProgress();
  }

  markLogoutExecuted(): void {
    const decision = this.logoutDecision();

    if (!this.sessionId() || !decision) {
      return;
    }

    this.postMessage({
      type: "LOGOUT_EXECUTED",
      logoutEpoch: decision.logoutEpoch,
      visible: document.visibilityState === "visible",
    });
  }

  private bindWorkerListeners(): void {
    if (this.workerListenersBound || !this.canUseServiceWorker()) {
      return;
    }

    navigator.serviceWorker.addEventListener(
      "message",
      this.handleWorkerMessage,
    );
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      this.handleControllerChange,
    );
    this.workerListenersBound = true;
  }

  private bindActivityListeners(): void {
    if (this.activityListenersBound || typeof window === "undefined") {
      return;
    }

    window.addEventListener("pointerdown", this.handlePointerActivity, {
      passive: true,
    });
    window.addEventListener("keydown", this.handleKeyboardActivity, {
      passive: true,
    });
    window.addEventListener("focus", this.handleFocusActivity, {
      passive: true,
    });
    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.addEventListener("pageshow", this.handlePageShow, {
      passive: true,
    });
    // Page Lifecycle API: a frozen tab can be unfrozen while still hidden, which
    // fires neither visibilitychange, focus, nor pageshow. Without this the
    // deadline would only be re-checked on the next (throttled) ticker gap.
    document.addEventListener("resume", this.handleResume);
    this.activityListenersBound = true;
  }

  private startTicker(): void {
    if (this.tickHandle !== null || typeof window === "undefined") {
      return;
    }

    this.lastTickAt = Date.now();

    this.tickHandle = window.setInterval(() => {
      const now = Date.now();
      const elapsedSinceLastTick = now - this.lastTickAt;
      this.lastTickAt = now;

      this.currentTime.set(now);

      if (!this.sessionId() || this.logoutInProgress()) {
        return;
      }

      const phase = this.phase();

      // A gap far larger than the interval means the tab was throttled or
      // suspended (background/frozen). Treat the first tick after such a gap
      // like a wake so we don't silently sail past warningAt/expiresAt.
      const wokeFromFreeze =
        elapsedSinceLastTick > IdleSessionService.FREEZE_GAP_MS;

      if (wokeFromFreeze) {
        // The ticker stopped executing for far longer than its interval — the
        // signature of a throttled/suspended/frozen tab. Persist when and for
        // how long so a missed deadline can be attributed after the fact.
        void this.logIdleEvent({
          type: "freeze-detected",
          source: "ticker",
          gapMs: elapsedSinceLastTick,
          phaseAfter: phase,
        });
      }

      if (phase === "timedOut") {
        this.postMessage({
          type: "REQUEST_EVALUATION",
          visible: document.visibilityState === "visible",
          config: this.getConfig(),
        });
      } else if (wokeFromFreeze && phase === "warning") {
        // Pull the authoritative snapshot; another tab may have extended it.
        this.requestStateSync();
      }
    }, 1000);
  }

  private requestStateSync(): void {
    if (!this.sessionId()) {
      return;
    }

    this.postMessage({
      type: "REQUEST_STATE",
      visible: document.visibilityState === "visible",
      config: this.getConfig(),
    });
  }

  private readonly handleWorkerMessage = (
    event: MessageEvent<IdleWorkerServerMessage>,
  ): void => {
    const message = event.data;

    if (!message || !this.sessionId() || message.sessionId !== this.sessionId()) {
      return;
    }

    if (message.type === "STATE_CLEARED") {
      this.clearLocalState();
      return;
    }

    this.applySnapshot(message.snapshot, message.type === "FORCE_LOGOUT");
  };

  private readonly handleControllerChange = (): void => {
    this.requestStateSync();
  };

  private readonly handlePointerActivity = (): void => {
    this.recordActivity("pointer");
  };

  private readonly handleKeyboardActivity = (): void => {
    this.recordActivity("keyboard");
  };

  private readonly handleFocusActivity = (): void => {
    this.onWake("focus");
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      this.onWake("visibility");
    }
  };

  private readonly handlePageShow = (): void => {
    // Fires on initial load and when the page is restored from the bfcache,
    // where timers were suspended entirely.
    this.onWake("pageshow");
  };

  private readonly handleResume = (): void => {
    // Page Lifecycle "resume": a frozen tab was thawed. It may still be hidden,
    // so this can be the only signal that the ticker was suspended.
    this.onWake("resume");
  };

  /**
   * Regaining focus/visibility/page restore is the start of a fresh interaction,
   * so it flows through the same `recordActivity` rule as pointer/keyboard: it
   * resets the deadline while active, is ignored during the warning countdown,
   * and can never resurrect an already-expired session (that path coordinates
   * logout instead). `recordActivity` refreshes the wall clock first, so the
   * decision is made on real elapsed time rather than the frozen ticker value.
   */
  private onWake(source: string): void {
    if (!this.sessionId()) {
      return;
    }

    // Capture the phase as the (possibly stale) ticker last left it, before
    // recordActivity refreshes the clock — the delta is the key diagnostic
    // signal that a tab was suspended past a deadline.
    const phaseBefore = this.phase();

    this.recordActivity(source);

    void this.logIdleEvent({
      type: "wake",
      source,
      phaseBefore,
      phaseAfter: this.phase(),
    });
  }

  private applySnapshot(snapshot: IdleSessionSnapshot, forceLogout: boolean): void {
    const currentSnapshot = this.snapshotState();

    if (
      currentSnapshot &&
      snapshot.version < currentSnapshot.version &&
      !forceLogout
    ) {
      return;
    }

    this.currentTime.set(Date.now());
    this.snapshotState.set(snapshot);

    if (snapshot.logoutInProgress) {
      this.logoutDecision.set({
        logoutEpoch: snapshot.logoutEpoch,
        executorTabId: snapshot.logoutExecutorTabId ?? null,
      });
    }

    if (forceLogout || snapshot.logoutInProgress) {
      this.routeToLogout();
    }
  }

  private routeToLogout(): void {
    this.zone.run(() => {
      if (this.router.url !== "/logout") {
        this.router.navigate(["/logout"]);
      }
    });
  }

  private getConfig(): IdleSessionConfig {
    return {
      idleMs: envModule.environment.timeoutSession * 1000,
      warningMs: envModule.environment.timeoutIdle * 1000,
    };
  }

  private applyLocalActivity(): void {
    const now = Date.now();
    const snapshot = this.snapshotState() || this.createLocalSnapshot(now);

    this.currentTime.set(now);
    this.logoutDecision.set(null);
    this.snapshotState.set({
      ...snapshot,
      version: snapshot.version + 1,
      lastActivityAt: now,
      warningAt: now + snapshot.idleMs,
      expiresAt: now + snapshot.idleMs + snapshot.warningMs,
      phase: "active",
      logoutInProgress: false,
      logoutReason: undefined,
      logoutExecutorTabId: null,
      updatedAt: now,
    });
  }

  private applyLocalLogout(reason: IdleLogoutReason): void {
    const now = Date.now();
    const snapshot = this.snapshotState() || this.createLocalSnapshot(now);
    const logoutEpoch = snapshot.logoutEpoch + 1;

    this.currentTime.set(now);
    this.snapshotState.set({
      ...snapshot,
      version: snapshot.version + 1,
      phase: "timedOut",
      logoutInProgress: true,
      logoutEpoch,
      logoutReason: reason,
      logoutExecutorTabId: this.tabId,
      updatedAt: now,
    });
    this.logoutDecision.set({
      logoutEpoch,
      executorTabId: this.tabId,
    });
    this.routeToLogout();
  }

  private createLocalSnapshot(now: number): IdleSessionSnapshot {
    const currentSessionId = this.sessionId() || "local-idle-session";
    const config = this.getConfig();

    return {
      sessionId: currentSessionId,
      version: 0,
      lastActivityAt: now,
      warningAt: now + config.idleMs,
      expiresAt: now + config.idleMs + config.warningMs,
      idleMs: config.idleMs,
      warningMs: config.warningMs,
      phase: "active",
      logoutInProgress: false,
      logoutEpoch: 0,
      logoutExecutorTabId: null,
      updatedAt: now,
    };
  }

  private async waitForWorkerReady(): Promise<boolean> {
    if (!this.canUseServiceWorker()) {
      return false;
    }

    const hasController = navigator.serviceWorker.controller !== null;
    if (hasController) {
      return true;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      return Boolean(
        registration.active ||
          registration.waiting ||
          registration.installing,
      );
    } catch (error) {
      console.error("Idle session worker is not ready", error);
      return false;
    }
  }

  private postMessage(
    message: IdleWorkerClientPayload,
  ): void {
    const currentSessionId = this.sessionId();
    if (!currentSessionId || !this.canUseServiceWorker()) {
      return;
    }

    const payload: IdleWorkerClientMessage = {
      ...message,
      sessionId: currentSessionId,
      tabId: this.tabId,
      at: Date.now(),
    } as IdleWorkerClientMessage;

    const controller = navigator.serviceWorker.controller;
    if (controller) {
      controller.postMessage(payload);
      return;
    }

    navigator.serviceWorker.ready
      .then((registration) => {
        const target =
          registration.active ||
          registration.waiting ||
          registration.installing;

        target?.postMessage(payload);
      })
      .catch((error) => {
        console.error("Failed to post idle session message", error);
      });
  }

  private canUseServiceWorker(): boolean {
    return typeof window !== "undefined" && "serviceWorker" in navigator;
  }

  private async clearPersistedSessionState(sessionId: string): Promise<void> {
    if (typeof indexedDB === "undefined") {
      return;
    }

    const database = await this.openIdleSessionDatabase();

    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction("idle-sessions", "readwrite");
      const store = transaction.objectStore("idle-sessions");

      store.delete(sessionId);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private async openIdleSessionDatabase(): Promise<IDBDatabase> {
    return await new Promise<IDBDatabase>((resolve, reject) => {
      // Version 2 adds the append-only `idle-events` history store. Both this
      // opener and the service worker's `openDatabase` request version 2 with
      // identical, idempotent upgrade logic, so whichever context opens the DB
      // first performs the full migration for the other.
      const request = indexedDB.open("ecolab-idle-session-db", 2);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains("idle-sessions")) {
          database.createObjectStore("idle-sessions", { keyPath: "sessionId" });
        }
        if (!database.objectStoreNames.contains("idle-events")) {
          const eventStore = database.createObjectStore("idle-events", {
            keyPath: "eventId",
            autoIncrement: true,
          });
          eventStore.createIndex("sessionId", "sessionId", { unique: false });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Append a diagnostic record to the shared history store. Best-effort and
   * fully isolated: any failure is swallowed so instrumentation can never
   * disrupt idle enforcement. The client only records what the worker cannot
   * observe — tab freeze/suspend and wake — while the worker owns the
   * authoritative lifecycle events (phase changes, resets, logout).
   */
  private async logIdleEvent(
    event: Partial<IdleHistoryEvent> & { type: IdleHistoryEventType },
  ): Promise<void> {
    if (typeof indexedDB === "undefined") {
      return;
    }

    const sessionId = this.sessionId();
    if (!sessionId) {
      return;
    }

    const snapshot = this.snapshotState();
    const record: IdleHistoryEvent = {
      sessionId,
      at: Date.now(),
      origin: "client",
      tabId: this.tabId,
      visible:
        typeof document !== "undefined"
          ? document.visibilityState === "visible"
          : undefined,
      version: snapshot?.version,
      warningAt: snapshot?.warningAt,
      expiresAt: snapshot?.expiresAt,
      ...event,
    };

    try {
      const database = await this.openIdleSessionDatabase();
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction("idle-events", "readwrite");
        transaction.objectStore("idle-events").add(record);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      });
    } catch (error) {
      console.debug("Failed to persist idle history event", error);
    }
  }

  /**
   * Retrieve the full recorded history for a session, oldest first, for
   * debugging why a logout did or did not fire. Combines client- and
   * worker-written entries (both live in the same store).
   */
  async getSessionHistory(
    sessionId: string | null = this.sessionId(),
  ): Promise<IdleHistoryEvent[]> {
    if (typeof indexedDB === "undefined" || !sessionId) {
      return [];
    }

    const database = await this.openIdleSessionDatabase();
    return await new Promise<IdleHistoryEvent[]>((resolve, reject) => {
      const transaction = database.transaction("idle-events", "readonly");
      const index = transaction.objectStore("idle-events").index("sessionId");
      const request = index.getAll(sessionId);

      request.onsuccess = () =>
        resolve(
          ((request.result as IdleHistoryEvent[]) ?? []).sort(
            (left, right) => left.at - right.at,
          ),
        );
      request.onerror = () => reject(request.error);
    });
  }

  private getOrCreateTabId(): string {
    if (typeof window === "undefined") {
      return "server";
    }

    const storageKey = "ecolab.envision_idleTabId";
    const existingTabId = window.sessionStorage.getItem(storageKey);
    if (existingTabId) {
      return existingTabId;
    }

    const nextTabId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.sessionStorage.setItem(storageKey, nextTabId);
    return nextTabId;
  }
}